import { useEffect, useRef } from 'react';

const book = {
  title: 'Endless Hope: A Look at Glaring Reality',
  series: 'Standalone',
  description:
    "Endless Hope shines a light on the universal struggles we all face at one point or another. At its core, humanity shares a timeless pursuit — for unshakeable faith, enduring hope, and unconditional love. In this concise collection of thought-provoking poems and poignant reflections, the author captures the urgency of life's journey: twisting, unpredictable, yet filled with moments of awe and inspiration.",
  format: 'Kindle Edition',
  pages: '63 Pages',
  language: 'English',
  publisher: 'Leavitt Peak Press',
  published: 'September 10, 2025',
  isbn: '978-1969870628',
  price: '$3.38',
  amazonUrl: 'https://www.amazon.com/dp/B0FQRNYL93',
  coverUrl:
    'https://public.readdy.ai/ai/img_res/50a69996ed4843b18eece71625222042.jpg',
  themes: [
    {
      icon: 'ri-heart-line',
      label: 'Faith & Hope',
      desc: "A timeless pursuit for unshakeable faith and enduring hope in the face of life's challenges.",
    },
    {
      icon: 'ri-sun-foggy-line',
      label: 'Glaring Reality',
      desc: 'An honest look at the universal struggles humanity shares — raw, unfiltered, and deeply human.',
    },
    {
      icon: 'ri-quill-pen-line',
      label: 'Poetic Reflection',
      desc: "Thought-provoking poems and poignant reflections that capture life's twisting, unpredictable journey.",
    },
    {
      icon: 'ri-sparkling-line',
      label: 'Unconditional Love',
      desc: 'Moments of awe and inspiration woven through every page, pointing toward love as the ultimate anchor.',
    },
  ],
};

const bookDetails = [
  { icon: 'ri-file-text-line', label: 'Format', value: book.format },
  { icon: 'ri-pages-line', label: 'Print Length', value: book.pages },
  { icon: 'ri-global-line', label: 'Language', value: book.language },
  { icon: 'ri-building-line', label: 'Publisher', value: book.publisher },
  { icon: 'ri-calendar-line', label: 'Published', value: book.published },
  { icon: 'ri-barcode-line', label: 'ISBN-13', value: book.isbn },
];

export default function OtherBooksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-other');
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  return (
    <section id="other-books" className="py-24 md:py-32 bg-stone-950 relative overflow-hidden">
      {/* Decorative glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/4 rounded-full blur-3xl pointer-events-none"></div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-10 relative z-10">
        {/* Header */}
        <div
          className="reveal-other text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            Also by Kevin Hamlett
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4 leading-tight">
            More{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
              Published Works
            </span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Explore Kevin Hamlett&apos;s other published titles — each a unique journey into faith, hope, and the depths of the human experience.
          </p>
        </div>

        {/* Book layout — sticky left, scrollable right */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* LEFT: Sticky cover + buy panel */}
          <div
            className="reveal-other flex-shrink-0 flex flex-col items-center gap-6 w-full lg:w-auto lg:sticky lg:top-28"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            {/* Cover */}
            <div className="relative group">
              <div className="absolute -inset-6 bg-amber-400/10 rounded-3xl blur-2xl group-hover:bg-amber-400/20 transition-all duration-500"></div>
              <img
                src={book.coverUrl}
                alt={`${book.title} Book Cover`}
                className="relative w-52 md:w-64 h-auto rounded-xl object-cover object-top transform group-hover:-translate-y-2 transition-transform duration-500"
              />
            </div>

            {/* Pricing */}
            <div className="w-full max-w-xs bg-stone-900 rounded-2xl p-6 border border-stone-800 text-center">
              <div className="flex items-center justify-center gap-3 mb-1">
                <span className="text-3xl font-bold text-amber-400">{book.price}</span>
              </div>
              <p className="text-stone-500 text-xs mb-5">Kindle Edition · Instant Download</p>
              <a
                href={book.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-amazon-line text-base"></i>
                Buy on Amazon
              </a>
            </div>

            {/* Book details */}
            <div className="w-full max-w-xs space-y-2">
              {bookDetails.map((d) => (
                <div key={d.label} className="flex items-center gap-3 py-2 border-b border-stone-800/60">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${d.icon} text-amber-500 text-sm`}></i>
                  </div>
                  <span className="text-stone-500 text-xs w-24 flex-shrink-0">{d.label}</span>
                  <span className="text-stone-300 text-xs font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Scrollable content */}
          <div className="flex-1">
            {/* Series badge + title */}
            <div
              className="reveal-other mb-10"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 rounded-full bg-stone-800 text-stone-400 text-xs border border-stone-700 font-medium">
                  {book.series}
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-5 leading-snug">
                {book.title}
              </h3>
              <h4 className="text-xl font-serif font-bold text-amber-300 mb-4">About This Book</h4>
              <div className="space-y-4 text-stone-300 text-base leading-relaxed">
                <p>{book.description}</p>
                <p>
                  With practical wisdom and philosophical depth, <em>Endless Hope</em> offers readers a look at glaring reality — and the strength to meet it with an endless hope that uplifts and comforts the soul.
                </p>
              </div>
            </div>

            {/* Themes */}
            <div
              className="reveal-other"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <h4 className="text-xl font-serif font-bold text-amber-300 mb-6">Core Themes</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {book.themes.map((t) => (
                  <div
                    key={t.label}
                    className="flex gap-4 p-5 rounded-xl bg-stone-900/60 border border-stone-800/50 hover:border-amber-500/30 transition-all duration-300"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0">
                      <i className={`${t.icon} text-amber-400 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm mb-1">{t.label}</div>
                      <div className="text-stone-400 text-xs leading-relaxed">{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <div
              className="reveal-other mt-8 p-5 rounded-xl bg-amber-500/5 border border-amber-500/20"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <div className="flex items-start gap-3">
                <i className="ri-double-quotes-l text-amber-400 text-2xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-stone-300 text-sm leading-relaxed italic">
                    &ldquo;Humanity shares a timeless pursuit — for unshakeable faith, enduring hope, and unconditional love.&rdquo;
                  </p>
                  <p className="text-amber-400 text-xs font-semibold mt-2">— Endless Hope</p>
                </div>
              </div>
            </div>

            {/* Series note */}
            <div
              className="reveal-other mt-6 p-5 rounded-xl bg-stone-900/60 border border-stone-800"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-amber-400 text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-amber-300 font-semibold text-sm mb-1">A Standalone Work</p>
                  <p className="text-stone-400 text-sm leading-relaxed">
                    <em>Endless Hope</em> stands on its own as a complete and deeply personal collection — no prior reading required. Perfect for anyone seeking a moment of reflection, clarity, or renewed faith.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
